import JSZip from "jszip";
import QRCodeLib from "qrcode";
import { saveAs } from "file-saver";

export interface BulkQRGenerateOptions {
  assetName: string;
  items: Record<string, any>[];
  nameKey: string;
  idKey: string;
  zipName?: string;
  logoSrc?: string;
}

/**
 * Generates QR codes for a list of items and downloads them as a ZIP file.
 */
export async function bulkGenerateAndDownloadQR({
  assetName,
  items,
  nameKey,
  idKey,
  zipName = "qr-codes.zip",
  logoSrc = "/captiva-mark.png",
}: BulkQRGenerateOptions): Promise<void> {
  if (!items || !items.length) return;
  const zip = new JSZip();
  const container = document.createElement('div');
  container.style.display = 'none';
  document.body.appendChild(container);
  for (const item of items) {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 400;
    container.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      container.removeChild(canvas);
      continue;
    }
    const qrValue = `${assetName}:${item[idKey]}`;
    await QRCodeLib.toCanvas(canvas, qrValue, {
      width: 400,
      margin: 10,
      errorCorrectionLevel: 'H'
    });
    // Draw logo
    const logoImg = new window.Image();
    logoImg.src = logoSrc;
    await new Promise((resolve) => {
      logoImg.onload = resolve;
      logoImg.onerror = resolve;
    });
    const logoX = 20;
    const logoY = 20;
    const logoSize = 40;
    ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
    ctx.font = "bold 16px 'Helvetica Neue', Helvetica, Arial, sans-serif";
    ctx.fillStyle = "#333";
    ctx.textBaseline = "middle";
    ctx.fillText(`${assetName} - ${item[nameKey]}`, logoX + logoSize + 16, logoY + logoSize / 2);
    const pngUrl = canvas.toDataURL("image/png");
    zip.file(`${assetName}-${item[nameKey]}-qr.png`, pngUrl.substring(pngUrl.indexOf(',') + 1), {base64: true});
    container.removeChild(canvas);
  }
  document.body.removeChild(container);
  const zipBlob = await zip.generateAsync({type: "blob"});
  saveAs(zipBlob, zipName);
}

export async function generateAndDownloadSingleQR(
  assetName: string,
  itemId: string,
  itemName: string,
  logoSrc = "/captiva-mark.png"
): Promise<void> {
  const canvas = document.createElement('canvas');
  canvas.width = 400;
  canvas.height = 400;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const qrValue = `${assetName}:${itemId}`;
  await QRCodeLib.toCanvas(canvas, qrValue, {
    width: 400,
    margin: 10,
    errorCorrectionLevel: 'H'
  });
  const logoImg = new window.Image();
  logoImg.src = logoSrc;
  await new Promise((resolve) => {
    logoImg.onload = resolve;
    logoImg.onerror = resolve;
  });
  const logoX = 20;
  const logoY = 20;
  const logoSize = 40;
  ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
  ctx.font = "bold 16px 'Helvetica Neue', Helvetica, Arial, sans-serif";
  ctx.fillStyle = "#333";
  ctx.textBaseline = "middle";
  ctx.fillText(`${assetName} - ${itemName}`, logoX + logoSize + 16, logoY + logoSize / 2);
  canvas.toBlob((blob) => {
    if (blob) saveAs(blob, `${assetName}-${itemName}-qr.png`);
  }, "image/png");
}
